import React, { useCallback, useMemo, useContext, Fragment } from 'react';
import { PatientChartContext } from '../../../contexts/patientChart';
import {
  PATIENT, PRESCRIBER, PRODUCT_INFO, SEND_TO_PS_REASONS,
  MODAL_KEY, CONFIRMATION_POPUP_KEY, RESPONSE_POPUP_KEY,
  DIMENSION, INTAKE, DOSE_SPOT, ALLOW_ADD_PRODUCT_TO_ORDER_APP_SETTING,
  TRUE,
  ADD_PRODUCT_INFO,
  IPRODUCT_COLLECTION,
} from '../../../constants/patientChart.constant';
import { ORDER_AND_PRESCRIBER_FLAGS, ORDERS, SAVED_FLAGS } from '../../../constants/visits.constant';
import { FEATURE } from '../../../utils/feature';
import { POPUP_TYPE } from '../../subComponents/popup';
import PPButton, { VARIENT } from '../../subComponents/button';
import Modal from '../../subComponents/commonModal/v3';
import { Approve, ApproveV2 } from '../Approve';
import Deny from '../Deny/v1';
import AddSoapNote from '../AddSoapNote/v1';
import AddNote from '../AddNote/v1';
import SendToPS from '../SendToPS/v1';
import AddProduct from '../AddProduct/v2';
import ChangeProduct from '../ChangeProduct';

const hasFeature = FEATURE();

export const Actions = ({
  orderId = 0,
  patientId = 0,
  doctorId = 0,
  orders = ORDERS(),
  assignedDoctorId = 0,
  isAssignedDoctorViewing = false,
  patient = PATIENT(),
  history = {},
  prescriber = PRESCRIBER(),
  product = PRODUCT_INFO(),
  productCost = {},
  addProductInfo = ADD_PRODUCT_INFO(),
  changeProducts = {},
  intake = INTAKE(),
  sendToPSReasons = SEND_TO_PS_REASONS(),
  emailTemplatesV2 = {
    data: [],
    subjects: {
      data: new Map(),
    },
  },
  approveEmailTemplatesV2 = {},
  denyEmailTemplatesV2 = {},
  emailReasons = {
    data: [],
  },
  soapTemplates= {},
  medicalOrderNotes = {},
  cmnTemplateFormField = {},
  approvalInfo = {},
  isSoapNoteAdded = false,
  savedFlags = SAVED_FLAGS(),
  flags = ORDER_AND_PRESCRIBER_FLAGS(),
  doseSpot = DOSE_SPOT(),
  quantityRangeForProduct = {},
  changeProductForOrderAPI = {},

  getScheduledVisits = async (requestBody = {}) => {},
  getAsyncOrders = async (requestBody = {}) => {},
  getCmnPdfData = async (requestBody = {}) => {},
  verifyPin = async (requestBody = {}) => {},
  approveOrder = async (requestBody = {}, callback = () => {}, routePath = '', called = true) => {},
  denyOrder = async (requestBody = {}) => {},
  addNote = async (requestBody = {}) => {},
  addSoapNote = async (requestBody = {}) => {},
  getSoapNote = async (requestBody = {}) => {},
  resetDoseSpot = async () => {},
  getApprovalInfo = async (requestBody = {}) => {},
  getNotesTabInfo = async () => {},
  setSoapNoteAdded = async (requestBody = {}) => {},
  sendToPS = async (requestBody = {}) => {},
  setCmnTemplateFormField = () => {},
  removeOrdersFromLV = (requestBody = {}) => {},
  getDoseSpot = async (requestBody = {}) => {},
  getProductCost = async () => {},
  resetProductCost = async () => {},
  addProduct = async () => {},
  getQuantity = async (requestBody = { OrderId: '', ProductId: '' }) => ({}),
  changeProduct = async (requestBody = {}) => ({}),
  getEmails = async () => {},
  modifyDoseSpotPrescription = async (requestBody = { OrderId: '', DoctorId: '' }) => ({}),
  handler = {
    route: () => {},
    loadProductsListing: () => {},
  },
}) => {
  const {
    flag,
    areFlagsValid,
    noOfRequiredFlags,
    noPendingTask,
    ModalCollection,
    handleModalOpen,
    handleMultipleModalClose,
  } = useContext(PatientChartContext);

  const allowApprove = useMemo(() => {
    if (!hasFeature.APPROVE) {
      return false;
    }

    const conditionA = (
      isAssignedDoctorViewing &&
      orders.all.collection.has(orderId) &&
      isSoapNoteAdded &&
      !flag.editMode &&
      noPendingTask
    );

    if (!conditionA) {
      return false;
    }

    if (noOfRequiredFlags === 0) {
      return true;
    }

    return areFlagsValid;
  }, [flag.editMode, orders, isAssignedDoctorViewing, isSoapNoteAdded, areFlagsValid, noOfRequiredFlags, noPendingTask]);

  const allowDeny = useMemo(() => {
    return isAssignedDoctorViewing && orders.all.collection.has(orderId) && isSoapNoteAdded && noPendingTask;
  }, [orders, isAssignedDoctorViewing, isSoapNoteAdded, noPendingTask]);

  const allowSendToPS = useMemo(() => {
    return isAssignedDoctorViewing && orders.all.collection.has(orderId);
  }, [orders, isAssignedDoctorViewing]);

  const { productsInAddProductList, productCanBeAddedCollection } = useMemo(() => {
    const productCanBeAddedCollection = new Map([[-1, IPRODUCT_COLLECTION()]]);

    if (!changeProducts.api.data.size) {
      return { productsInAddProductList: [], productCanBeAddedCollection };
    }

    const collection = new Map(changeProducts.api.data);
    const productMap = new Map(product.productMap);
    /**
     * @description filters the unique products along with all the required fields from reducer's cache.
     */
    [...collection.entries()].forEach(([targetId = 0, items = []]) => {
      const filteredItems = items.filter(({ Value }) => !collection.has(Value));
      filteredItems.forEach(({ Title, PrescriberProductDisplayName, Value: id }) => {
        const title = PrescriberProductDisplayName || Title;
        const targetProduct = productMap.get(targetId) || { AutRefill: false, Pharmacy: '', Shipping: '', Qty: 0 };
        const _product = {
          id,
          value: id,
          title,
          label: title,
          summary: {
            autoRefill: targetProduct.AutRefill,
            pharmacy: targetProduct.Pharmacy,
            shipper: targetProduct.Shipping,
            quantity: targetProduct.Qty,
          }
        };

        /**
         * @description Update if the `pharmacy` & `shipper` values were missing.
         */
        if (productCanBeAddedCollection.has(_product.id)) {
          const __product = productCanBeAddedCollection.get(_product.id);

          if (!__product.summary.pharmacy && _product.summary.pharmacy) {
            __product.summary.pharmacy = _product.summary.pharmacy;
          }

          if (!__product.summary.shipper && _product.summary.shipper) {
            __product.summary.shipper = _product.summary.shipper;
          }

          productCanBeAddedCollection.set(__product.id, __product);
        }

        if (!productCanBeAddedCollection.has(_product.id)) productCanBeAddedCollection.set(_product.id, _product);
      });
    });

    const _productsInAddProductList = [...productCanBeAddedCollection.values()].filter(({ id }) => id !== 0);
    return { productsInAddProductList: _productsInAddProductList, productCanBeAddedCollection };
  }, [product, changeProducts]);

  const allowAddProduct = useMemo(() => {
    if (!hasFeature.ADD_PRODUCT) {
      return false;
    }

    const conditionA = (
      isAssignedDoctorViewing &&
      orders.all.collection.has(orderId) &&
      changeProducts.api.data.size > 0 &&
      productsInAddProductList.length > 0
    );

    if (!conditionA) {
      return false;
    }

    const applicationSetting = (() => {
      try {
        return new Map(product.StoreFrontData.StorefrontApplicationSettings) || new Map();
      } catch (e) {
        return new Map();
      }
    })();

    if (!applicationSetting.has(ALLOW_ADD_PRODUCT_TO_ORDER_APP_SETTING)) {
      return false;
    }

    try {
      const config = applicationSetting.get(ALLOW_ADD_PRODUCT_TO_ORDER_APP_SETTING);
      return config.value.trim().toLowerCase() === TRUE;
    } catch (e) {
      // error handling is needed
    }

    return false;
  }, [product, orders, productsInAddProductList, changeProducts, isAssignedDoctorViewing]);

  const allowChangeProduct = useMemo(() => {
    if (!hasFeature.CHANGE_PRODUCT) {
      return false;
    }

    const conditionA = (
      isAssignedDoctorViewing &&
      orders.all.collection.has(orderId)
    );

    if (!conditionA) {
      return false;
    }

    return true;
  }, [orders, isAssignedDoctorViewing]);

  const allowJumpStart = useMemo(() => {
    if (!hasFeature.DOSESPOT_JUMP_START) {
      return false;
    }

    return true;
  }, []);

  const approveConfig = useMemo(() => {
    const {
      openCommonModal,
    } = doseSpot;

    const approveConfig = {
      style: DIMENSION.BIGGER4,
    };

    if (openCommonModal) {
      approveConfig.style = DIMENSION.BIGGER3;
    }

    return approveConfig;
  }, [doseSpot?.openCommonModal]);

  const ButtonsWithConfig = useMemo(() => [
    {
      Component: Approve,
      Button: PPButton,
      label: 'Approve',
      key: MODAL_KEY.APPROVE,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.APPROVE,
      responsePopupTitle: RESPONSE_POPUP_KEY.APPROVE,
      modalTitle: 'APPROVE',
      style: approveConfig.style,
      disabled: !allowApprove,
      visible: true,
    },
    {
      Component: Deny,
      Button: PPButton,
      label: 'Deny',
      key: MODAL_KEY.DENY,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.DENY,
      responsePopupTitle: RESPONSE_POPUP_KEY.DENY,
      modalTitle: 'DENY',
      style: DIMENSION.REGULAR2,
      disabled: !allowDeny,
      visible: true,
    },
    {
      Component: AddSoapNote,
      Button: PPButton,
      label: 'SOAP Note',
      key: MODAL_KEY.SOAP_NOTE,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.SOAP_NOTE,
      responsePopupTitle: RESPONSE_POPUP_KEY.SOAP_NOTE,
      modalTitle: 'SOAP NOTE',
      style: DIMENSION.REGULAR,
      disabled: false,
      visible: true,
    },
    {
      Component: AddNote,
      Button: PPButton,
      label: 'Add Note',
      key: MODAL_KEY.ADD_NOTE,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.ADD_NOTE,
      responsePopupTitle: RESPONSE_POPUP_KEY.ADD_NOTE,
      modalTitle: 'ADD NOTE',
      style: DIMENSION.SMALL,
      disabled: false,
      visible: true,
    },
    {
      Component: SendToPS,
      Button: PPButton,
      label: 'Send To PS',
      key: MODAL_KEY.SEND_TO_PS,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.SEND_TO_PS,
      responsePopupTitle: RESPONSE_POPUP_KEY.SEND_TO_PS,
      modalTitle: 'SEND ORDER TO PATIENT SUPPORT',
      style: DIMENSION.SMALL,
      disabled: !allowSendToPS,
      visible: true,
    },
    {
      Component: AddProduct,
      Button: PPButton,
      label: 'Add Product',
      key: MODAL_KEY.ADD_NEW_PRODUCT,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT_CLOSE,
      responsePopupTitle: RESPONSE_POPUP_KEY.ADD_NEW_PRODUCT,
      saveConfirmationTitle: CONFIRMATION_POPUP_KEY.ADD_NEW_PRODUCT,
      modalTitle: 'ADD NEW PRODUCT',
      style: DIMENSION.REGULAR2,
      disabled: !allowAddProduct,
      visible: allowAddProduct,
      variant: VARIENT.contained,
    },
    {
      Component: ChangeProduct,
      Button: () => <Fragment />,
      label: 'Change Product',
      key: MODAL_KEY.CHANGE_PRODUCT,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.CHANGE_PRODUCT_CLOSE,
      responsePopupTitle: RESPONSE_POPUP_KEY.CHANGE_PRODUCT,
      saveConfirmationTitle: CONFIRMATION_POPUP_KEY.CHANGE_PRODUCT,
      modalTitle: 'CHANGING PRESCRIBED PRODUCT',
      style: DIMENSION.REGULAR2,
      disabled: !allowChangeProduct,
      visible: allowChangeProduct,
      variant: VARIENT.contained,
    },
    {
      Component: ApproveV2,
      Button: () => <Fragment />,
      label: 'DoseSpot Jump Start',
      key: MODAL_KEY.DOSESPOT_JUMPSTART,
      closeConfirmationTitle: CONFIRMATION_POPUP_KEY.DOSESPOT_JUMPSTART_CLOSE,
      responsePopupTitle: RESPONSE_POPUP_KEY.DOSESPOT_JUMPSTART,
      modalTitle: 'DOSESPOT JUMP START',
      style: DIMENSION.BIGGER3,
      disabled: !allowJumpStart,
      visible: allowJumpStart,
    },
  ].filter(({ visible }) => visible), [
    isSoapNoteAdded,
    isAssignedDoctorViewing,
    allowApprove,
    allowDeny,
    allowSendToPS,
    allowJumpStart,
    approveConfig,
  ]);

  const createPopupConfig = useCallback((key = '') => {
    const popup = {
      has: false,
      heading: 'Message',
      type: POPUP_TYPE.INFO,
      message: 'API response appears here...',
      onConfirm: async () => {},
      onCancel: () => {},
    };

    if (!ModalCollection.has(key)) {
      return popup;
    }

    const pC = ModalCollection.get(key);
    pC.has = true;

    return pC;
  }, [ModalCollection]);

  const Template = ButtonsWithConfig.map(({
    key,
    label,
    modalTitle,
    Component,
    Button,
    style,
    closeConfirmationTitle,
    responsePopupTitle,
    saveConfirmationTitle,
    disabled,
    variant,
  }) => {
    const responsePopup = createPopupConfig(responsePopupTitle);
    const saveConfirmation = createPopupConfig(saveConfirmationTitle);

    return (
      <Fragment key={key}>
        <Modal
          title={modalTitle}
          open={ModalCollection.has(key)}
          style={style}
          closeConfirmation={ModalCollection.has(closeConfirmationTitle)}
          onCloseConfirmationYes={handleMultipleModalClose.bind(this, { keys: [key, closeConfirmationTitle] })}
          onCloseConfirmationNo={handleMultipleModalClose.bind(this, { keys: [closeConfirmationTitle] })}
          responsePopup={responsePopup?.has}
          responsePopupType={responsePopup?.type}
          responsePopupHeading={responsePopup?.heading}
          responsePopupMessage={responsePopup?.message}
          onResponsePopupOk={handleMultipleModalClose.bind(this, { keys: [responsePopupTitle] })}
          saveConfirmation={saveConfirmation?.has}
          saveConfirmationHeading={saveConfirmation?.heading}
          saveConfirmationMessage={saveConfirmation?.message}
          onSaveConfirmationYes={saveConfirmation?.onConfirm}
          onSaveConfirmationNo={saveConfirmation?.onCancel}
        >
          <Component
            orderId={orderId}
            patientId={patientId}
            doctorId={doctorId}
            patient={patient}
            history={history}
            prescriber={prescriber}
            product={product}
            productCost={productCost}
            addProductInfo={addProductInfo}
            changeProducts={changeProducts}
            quantityRangeForProduct={quantityRangeForProduct}
            changeProductForOrderAPI={changeProductForOrderAPI}
            productsInAddProductList={productsInAddProductList}
            productCanBeAddedCollection={productCanBeAddedCollection}
            intake={intake}
            approvalInfo={approvalInfo}
            medicalOrderNotes={medicalOrderNotes}
            cmnTemplateFormField={cmnTemplateFormField}
            assignedDoctorId={assignedDoctorId}
            isAssignedDoctorViewing={isAssignedDoctorViewing}
            isSoapNoteAdded={isSoapNoteAdded}
            sendToPSReasons={sendToPSReasons}
            emailTemplatesV2={emailTemplatesV2}
            approveEmailTemplatesV2={approveEmailTemplatesV2}
            denyEmailTemplatesV2={denyEmailTemplatesV2}
            emailReasons={emailReasons}
            soapTemplates={soapTemplates}
            savedFlags={savedFlags}
            flags={flags}
            doseSpot={doseSpot}
            verifyPin={verifyPin}
            approveOrder={approveOrder}
            denyOrder={denyOrder}
            addNote={addNote}
            addSoapNote={addSoapNote}
            getSoapNote={getSoapNote}
            getNotesTabInfo={getNotesTabInfo}
            setSoapNoteAdded={setSoapNoteAdded}
            sendToPS={sendToPS}
            getScheduledVisits={getScheduledVisits}
            getAsyncOrders={getAsyncOrders}
            getCmnPdfData={getCmnPdfData}
            getApprovalInfo={getApprovalInfo}
            setCmnTemplateFormField={setCmnTemplateFormField}
            removeOrdersFromLV={removeOrdersFromLV}
            getDoseSpot={getDoseSpot}
            resetDoseSpot={resetDoseSpot}
            getProductCost={getProductCost}
            resetProductCost={resetProductCost}
            addProduct={addProduct}
            getQuantity={getQuantity}
            changeProduct={changeProduct}
            getEmails={getEmails}
            modifyDoseSpotPrescription={modifyDoseSpotPrescription}
            handler={handler}
          />
        </Modal>
        
        <Button label={label} disabled={disabled} variant={variant} endGutter onClick={handleModalOpen.bind(this, { key, modal: {} })} />
      </Fragment>
    );
  });

  return (
    <div className='flex flex-fill align-items-flex-end justify-content-flex-start gap-24'>
      <div className='min-width-280 height-42'></div>
      <div className='flex height-42 p-b-3 scroll-y-auto'>{Template}</div>
    </div>
  );
};

export default Actions;
